import {
	CLUSTER_CODE_MAX,
	CLUSTER_NAME_MAX,
	CLUSTER_SORT_ORDER_MAX,
	type Cluster,
	type ClusterDriveMinutes,
	DRIVE_MINUTES_MAX,
	DRIVE_MINUTES_MIN,
	TRANSPORT_REMARKS_MAX,
	TRANSPORT_SETTINGS_RANGES,
	makeCreateClusterRequest,
	makePatchClusterRequest,
	makeSetDriveMinutesRequest,
} from "@seagames/contracts";
import { Field, IconButton, Icons, Modal, ModalActions, useUiStrings } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "../../lib/api/client";
import { errorMessage } from "../../lib/api/messages";
import { clustersListOptions, createCluster, deleteCluster, driveMinutesOptions, invalidateFleet, putDriveMinutes, transportSettingsOptions, updateCluster } from "../../lib/api/transport-fleet";
import { toast } from "../../lib/ui/toast";
import { conflictField } from "../common/apply-api-error";
import { SheetSubhead, inputClass, routeSaveError, useProblemText } from "./form-bits";
import { useTransportFleetFormMessages as useFleetFormMessages } from "../../lib/i18n/use-form-schemas";
import { type MatrixDraft, type Problems, badCells, cellKey, checkCluster, hasProblems, issuesToProblems, matrixDraftFrom, matrixRows, normalizeClusterCode, sameMatrix, setCell } from "./model";

/**
 * Clusters & drive times (her `clusterSheet`, l3.js:196, plus PLAN X4):
 *
 *   Clusters     the areas venues, entry points, places, vehicles and drivers
 *                belong to — her list with usage, edit / delete, and the add
 *                form under it. Delete is refused (409) while anything uses it.
 *   Drive times  the cluster × cluster matrix (`cluster_drive_minutes`) that
 *                times every transfer. An empty cell is the games default
 *                (`transport_settings.default_drive_minutes`). The diagonal
 *                (dashed) is a trip inside one cluster. Directional; typing
 *                one cell fills its mirror unless the pair was split.
 *                Saving is ONE replace-set PUT.
 */
export function ClustersModal({ gamesId, onClose }: { gamesId: string; onClose: () => void }) {
	const { t } = useTranslation("transportFleet");
	const { t: tc } = useTranslation("common");
	const [panel, setPanel] = useState<"clusters" | "drive">("clusters");
	const clusters = useQuery(clustersListOptions());
	const matrix = useQuery(driveMinutesOptions());
	const settings = useQuery(transportSettingsOptions(gamesId));
	const items = clusters.data?.items ?? [];
	const editor = useClusterEditor(items.length);
	const grid = useMatrixEditor(matrix.data?.items, items);
	const fallback = settings.data?.default_drive_minutes ?? TRANSPORT_SETTINGS_RANGES.default_drive_minutes.default;
	const busy = editor.busy || grid.busy;

	const footer =
		panel === "clusters" ? (
			<ModalActions>
				{editor.editing ? (
					<button type="button" className="btn btn-line btn-sm" onClick={editor.reset} disabled={busy}>
						{t("clusters.cancelEdit")}
					</button>
				) : (
					<button type="button" className="btn btn-line btn-sm" onClick={onClose} disabled={busy}>
						{tc("action.close")}
					</button>
				)}
				<button type="submit" form="cl-form" className="btn btn-primary btn-sm" disabled={busy || clusters.isPending}>
					{editor.busy && <span className="loading loading-spinner loading-xs" />}
					{editor.editing ? t("clusters.save") : t("clusters.add")}
				</button>
			</ModalActions>
		) : (
			<ModalActions>
				<button type="button" className="btn btn-line btn-sm" onClick={onClose} disabled={busy}>
					{tc("action.close")}
				</button>
				<button type="button" className="btn btn-primary btn-sm" onClick={() => void grid.save()} disabled={busy || !grid.dirty || grid.bad.size > 0}>
					{grid.busy && <span className="loading loading-spinner loading-xs" />}
					{t("drive.save")}
				</button>
			</ModalActions>
		);

	return (
		<Modal open onClose={onClose} size="lg" title={t("clusters.title")} description={t("clusters.description")} dismissible={!busy} footer={footer}>
			<div className="grid gap-[1rem]">
				<div role="tablist" aria-label={t("clusters.title")} className="inline-flex w-fit gap-[0.25rem] rounded-[0.625rem] bg-gms-soft-2 p-[0.25rem]">
					{(["clusters", "drive"] as const).map((k) => (
						<button
							key={k}
							type="button"
							role="tab"
							aria-selected={panel === k}
							onClick={() => setPanel(k)}
							className={`inline-flex h-[2rem] cursor-pointer items-center rounded-[0.5rem] px-[0.875rem] text-[0.8125rem] outline-none focus-visible:ring-2 focus-visible:ring-gms-navy/40 ${
								panel === k ? "bg-base-100 font-semibold text-gms-navy shadow-[0_1px_2px_rgba(18,20,63,0.12),0_0_0_1px_var(--color-gms-line)]" : "font-medium text-gms-ink-2 hover:text-gms-navy-ink"
							}`}
						>
							{t(`clusters.panel.${k}`)}
						</button>
					))}
				</div>
				{clusters.isPending ? (
					<div className="skeleton h-[12rem] rounded-[0.625rem]" />
				) : clusters.isError ? (
					<LoadError error={clusters.error} onRetry={() => void clusters.refetch()} />
				) : panel === "clusters" ? (
					<ClustersPanel items={items} editor={editor} />
				) : matrix.isPending ? (
					<div className="skeleton h-[12rem] rounded-[0.625rem]" />
				) : matrix.isError ? (
					<LoadError error={matrix.error} onRetry={() => void matrix.refetch()} />
				) : items.length === 0 ? (
					<p className="rounded-field bg-gms-soft px-[0.875rem] py-[0.75rem] text-[0.8125rem] text-gms-ink-2">{t("drive.noClusters")}</p>
				) : (
					<MatrixGrid items={items} grid={grid} fallback={fallback} />
				)}
			</div>
		</Modal>
	);
}

function LoadError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
	const { t } = useTranslation("common");
	return (
		<div role="alert" className="flex items-center justify-between gap-[1rem] rounded-field bg-error/8 px-[0.875rem] py-[0.75rem] text-sm">
			<span>{errorMessage(error)}</span>
			<button type="button" className="btn btn-xs btn-outline" onClick={onRetry}>
				{t("action.retry")}
			</button>
		</div>
	);
}

// ── Clusters ────────────────────────────────────────────────────────────────

type ClusterField = "code" | "name" | "remarks" | "sort_order";
const CLUSTER_FIELDS: readonly ClusterField[] = ["code", "name", "remarks", "sort_order"];

interface ClusterDraft {
	code: string;
	name: string;
	remarks: string;
	sort_order: string;
}
const emptyCluster = (n: number): ClusterDraft => ({ code: "", name: "", remarks: "", sort_order: String((n + 1) * 10) });

function useClusterEditor(count: number) {
	const { t } = useTranslation("transportFleet");
	const messages = useFleetFormMessages();
	const [editing, setEditing] = useState<Cluster | null>(null);
	const [draft, setDraft] = useState<ClusterDraft>(() => emptyCluster(count));
	const [problems, setProblems] = useState<Problems<ClusterField>>({});
	const [busy, setBusy] = useState(false);
	const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

	const reset = () => {
		setEditing(null);
		setDraft(emptyCluster(count));
		setProblems({});
	};
	const startEdit = (c: Cluster) => {
		setEditing(c);
		setDraft({ code: c.code, name: c.name, remarks: c.remarks ?? "", sort_order: String(c.sort_order) });
		setProblems({});
	};
	const set = (k: ClusterField, v: string) => {
		setDraft((d) => ({ ...d, [k]: v }));
		setProblems((p) => ({ ...p, [k]: undefined }));
	};

	const save = async () => {
		const found: Problems<ClusterField> = { ...checkCluster(draft) };
		if (!/^\d+$/.test(draft.sort_order.trim()) || Number(draft.sort_order) > CLUSTER_SORT_ORDER_MAX) found.sort_order = { key: "invalidValue" };
		setProblems(found);
		if (hasProblems(found)) return;
		const body = { code: normalizeClusterCode(draft.code), name: draft.name, remarks: draft.remarks, sort_order: Number(draft.sort_order) };
		setBusy(true);
		try {
			if (editing) {
				const parsed = makePatchClusterRequest(messages).safeParse({ ...body, reason: `Changed cluster ${editing.code}` });
				if (!parsed.success) {
					setProblems(issuesToProblems(parsed.error.issues, CLUSTER_FIELDS, "name"));
					return;
				}
				await updateCluster(editing.cluster_id, parsed.data);
				toast.success(t("clusters.toast.updated", { name: parsed.data.name ?? editing.name }));
			} else {
				const parsed = makeCreateClusterRequest(messages).safeParse(body);
				if (!parsed.success) {
					setProblems(issuesToProblems(parsed.error.issues, CLUSTER_FIELDS, "name"));
					return;
				}
				await createCluster(parsed.data);
				toast.success(t("clusters.toast.added", { name: parsed.data.name }));
			}
			await invalidateFleet();
			reset();
		} catch (err) {
			routeSaveError(err, CLUSTER_FIELDS, setProblems, editing ? t("clusters.toast.updateFailed") : t("clusters.toast.addFailed"), (e) => {
				const f = conflictField(e);
				return f === "code" ? { code: { key: "clusterCodeTaken" } } : f === "name" ? { name: { key: "clusterNameTaken" } } : null;
			});
		} finally {
			setBusy(false);
		}
	};

	const remove = async (c: Cluster) => {
		setBusy(true);
		try {
			await deleteCluster(c.cluster_id);
			await invalidateFleet();
			toast.success(t("clusters.toast.deleted", { name: c.name }));
			if (editing?.cluster_id === c.cluster_id) reset();
		} catch (err) {
			toast.error(err instanceof ApiError && err.code === "CONFLICT" ? new Error(t("clusters.inUse")) : err, t("clusters.toast.deleteFailed"));
		} finally {
			setBusy(false);
			setConfirmDelete(null);
		}
	};

	return { editing, draft, problems, busy, confirmDelete, setConfirmDelete, reset, startEdit, set, save, remove };
}

type ClusterEditor = ReturnType<typeof useClusterEditor>;

function ClustersPanel({ items, editor }: { items: Cluster[]; editor: ClusterEditor }) {
	const { t } = useTranslation("transportFleet");
	const { t: tc } = useTranslation("common");
	const problemText = useProblemText();
	const nameRef = useRef<HTMLInputElement>(null);
	const { editing, draft, problems, busy, confirmDelete } = editor;

	return (
		<form
			id="cl-form"
			onSubmit={(e) => {
				e.preventDefault();
				void editor.save();
			}}
			noValidate
			className="grid gap-[1rem]"
		>
			{items.length === 0 ? (
				<p className="rounded-field bg-gms-soft px-[0.875rem] py-[0.75rem] text-[0.8125rem] text-gms-ink-2">{t("clusters.none")}</p>
			) : (
				<ul className="divide-y divide-gms-line rounded-field border border-gms-line">
					{items.map((c) => {
						const used = c.vehicle_count + c.driver_count + c.venue_count + c.entry_point_count + c.place_count;
						return (
							<li key={c.cluster_id} className={`flex items-start gap-[0.75rem] px-[0.875rem] py-[0.625rem] ${editing?.cluster_id === c.cluster_id ? "bg-gms-navy-soft" : ""}`}>
								<span className="mt-[0.0625rem] inline-flex h-[1.375rem] min-w-[2.75rem] shrink-0 items-center justify-center rounded-[0.25rem] bg-gms-navy px-[0.375rem] text-[0.6875rem] font-bold tracking-[0.06em] text-white">
									{c.code}
								</span>
								<span className="grid min-w-0 flex-1 gap-[0.125rem]">
									<b className="text-[0.875rem] font-semibold text-gms-navy-ink">{c.name}</b>
									{c.remarks && <span className="text-[0.78125rem] text-gms-ink-2">{c.remarks}</span>}
									<UsageLine c={c} />
								</span>
								{confirmDelete === c.cluster_id ? (
									<span className="flex shrink-0 flex-wrap items-center justify-end gap-[0.375rem]">
										<span className="text-[0.78125rem] text-gms-ink-2">{t("clusters.confirmDelete")}</span>
										<button type="button" className="btn btn-error btn-xs" disabled={busy} onClick={() => void editor.remove(c)}>
											{t("clusters.delete")}
										</button>
										<button type="button" className="btn btn-line btn-xs" disabled={busy} onClick={() => editor.setConfirmDelete(null)}>
											{t("clusters.keep")}
										</button>
									</span>
								) : (
									<span className="flex shrink-0 items-center gap-[0.125rem]">
										<IconButton
											label={t("clusters.editLabel", { name: c.name })}
											icon={<Icons.Pencil />}
											size="xs"
											variant="row"
											onClick={() => {
												editor.startEdit(c);
												nameRef.current?.focus();
											}}
										/>
										<IconButton
											label={used > 0 ? t("clusters.deleteInUse", { name: c.name }) : t("clusters.deleteLabel", { name: c.name })}
											icon={<Icons.Trash />}
											size="xs"
											variant="rowDanger"
											disabled={used > 0}
											onClick={() => editor.setConfirmDelete(c.cluster_id)}
										/>
									</span>
								)}
							</li>
						);
					})}
				</ul>
			)}

			<div className="grid gap-[0.75rem] rounded-field border border-gms-line bg-gms-soft px-[0.875rem] py-[0.75rem] sm:grid-cols-[8rem_minmax(0,1fr)_6rem]">
				<div className="sm:col-span-3">
					<SheetSubhead>{editing ? t("clusters.editTitle", { name: editing.name }) : t("clusters.addTitle")}</SheetSubhead>
				</div>
				<Field label={t("clusters.code")} htmlFor="cl-code" required error={problemText(problems.code)} hint={t("clusters.codeHint")}>
					<input
						id="cl-code"
						value={draft.code}
						onChange={(e) => editor.set("code", e.target.value.toUpperCase())}
						maxLength={CLUSTER_CODE_MAX}
						autoComplete="off"
						spellCheck={false}
						className={inputClass(!!problems.code, "uppercase tracking-[0.06em]")}
					/>
				</Field>
				<Field label={t("clusters.name")} htmlFor="cl-name" required error={problemText(problems.name)}>
					<input ref={nameRef} id="cl-name" value={draft.name} onChange={(e) => editor.set("name", e.target.value)} maxLength={CLUSTER_NAME_MAX} autoComplete="off" className={inputClass(!!problems.name)} />
				</Field>
				<Field label={t("clusters.order")} htmlFor="cl-order" required error={problemText(problems.sort_order)}>
					<input
						id="cl-order"
						type="number"
						inputMode="numeric"
						min={0}
						max={CLUSTER_SORT_ORDER_MAX}
						value={draft.sort_order}
						onChange={(e) => editor.set("sort_order", e.target.value)}
						className={inputClass(!!problems.sort_order, "tabular-nums")}
					/>
				</Field>
				<Field label={t("clusters.remarks")} htmlFor="cl-remarks" aside={tc("state.optional")} error={problemText(problems.remarks)} className="sm:col-span-3">
					<input
						id="cl-remarks"
						value={draft.remarks}
						onChange={(e) => editor.set("remarks", e.target.value)}
						maxLength={TRANSPORT_REMARKS_MAX}
						autoComplete="off"
						placeholder={t("clusters.remarksPlaceholder")}
						className={inputClass(!!problems.remarks)}
					/>
				</Field>
			</div>
		</form>
	);
}

/** "62 vehicles · 62 drivers · 3 venues" — only what uses it; "Not used yet" otherwise. */
function UsageLine({ c }: { c: Cluster }) {
	const { t } = useTranslation("transportFleet");
	const { formatNumber: n } = useUiStrings();
	const parts = [
		c.vehicle_count > 0 ? t("clusters.usage.vehicles", { count: c.vehicle_count, n: n(c.vehicle_count) }) : null,
		c.driver_count > 0 ? t("clusters.usage.drivers", { count: c.driver_count, n: n(c.driver_count) }) : null,
		c.venue_count > 0 ? t("clusters.usage.venues", { count: c.venue_count, n: n(c.venue_count) }) : null,
		c.entry_point_count > 0 ? t("clusters.usage.entryPoints", { count: c.entry_point_count, n: n(c.entry_point_count) }) : null,
		c.place_count > 0 ? t("clusters.usage.places", { count: c.place_count, n: n(c.place_count) }) : null,
	].filter((x): x is string => x !== null);
	return <span className="text-[0.75rem] text-gms-ink-3">{parts.length ? parts.join(" · ") : t("clusters.usage.none")}</span>;
}

// ── Drive times ─────────────────────────────────────────────────────────────

function useMatrixEditor(stored: ClusterDriveMinutes[] | undefined, items: Cluster[]) {
	const { t } = useTranslation("transportFleet");
	const messages = useFleetFormMessages();
	// null = follow what the server holds; the first keystroke takes a copy.
	const [own, setOwn] = useState<MatrixDraft | null>(null);
	const [mirror, setMirror] = useState(true);
	const [busy, setBusy] = useState(false);
	const draft = useMemo(() => own ?? matrixDraftFrom(stored ?? []), [own, stored]);
	const ids = items.map((c) => c.cluster_id);
	const bad = new Set(badCells(draft));
	const rows = matrixRows(draft, ids);
	const dirty = own !== null && !sameMatrix(rows, stored ?? []);

	const type = (from: string, to: string, value: string) => setOwn(setCell(draft, from, to, value, mirror));

	const save = async () => {
		if (bad.size > 0) return;
		const parsed = makeSetDriveMinutesRequest(messages).safeParse({ rows });
		if (!parsed.success) {
			toast.error(new Error(parsed.error.issues[0]?.message ?? ""), t("drive.saveFailed"));
			return;
		}
		setBusy(true);
		try {
			await putDriveMinutes(parsed.data.rows);
			await invalidateFleet();
			setOwn(null);
			toast.success(t("drive.saved"));
		} catch (err) {
			toast.error(err, t("drive.saveFailed"));
		} finally {
			setBusy(false);
		}
	};

	return { draft, mirror, setMirror, busy, bad, rows, dirty, type, save };
}

type MatrixState = ReturnType<typeof useMatrixEditor>;

/** The tint of a filled cell: how far the pair is, relative to the longest drive (4 steps of the navy ramp). */
const TINTS = ["bg-[#f3f4fb]", "bg-[#e6e8f7]", "bg-[#d7daf2]", "bg-[#c5c9ec]"] as const;

function MatrixGrid({ items, grid, fallback }: { items: Cluster[]; grid: MatrixState; fallback: number }) {
	const { t } = useTranslation("transportFleet");
	const { formatNumber } = useUiStrings();
	const max = Math.max(fallback, ...grid.rows.map((r) => r.minutes));
	const tint = (v: number) => TINTS[Math.min(3, Math.max(0, Math.ceil((v / max) * 4) - 1))];

	return (
		<div className="grid gap-[0.875rem]">
			<div className="flex flex-wrap items-center justify-between gap-[0.75rem]">
				<p className="max-w-[34rem] text-[0.8125rem] leading-snug text-gms-ink-3">{t("drive.intro", { n: formatNumber(fallback) })}</p>
				<label className="inline-flex cursor-pointer items-center gap-[0.5rem] text-[0.8125rem] font-medium text-gms-navy-ink">
					<input type="checkbox" className="toggle toggle-xs toggle-primary" checked={grid.mirror} onChange={(e) => grid.setMirror(e.target.checked)} />
					{t("drive.mirror")}
				</label>
			</div>

			{/* Rows = from, columns = to. Sideways scroll inside the sheet, never the page. */}
			<div className="overflow-x-auto rounded-field border border-gms-line">
				<table className="w-full border-collapse text-[0.8125rem]">
					<caption className="sr-only">{t("drive.caption")}</caption>
					<thead>
						<tr className="bg-gms-soft">
							<th scope="col" className="sticky left-0 z-[1] min-w-[9rem] bg-gms-soft px-[0.75rem] py-[0.5rem] text-left text-[0.71875rem] font-semibold text-gms-ink-3">
								{t("drive.fromTo")}
							</th>
							{items.map((c) => (
								<th key={c.cluster_id} scope="col" title={c.name} className="min-w-[4.5rem] px-[0.375rem] py-[0.5rem] text-center text-[0.75rem] font-bold tracking-[0.04em] text-gms-navy">
									{c.code}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{items.map((from) => (
							<tr key={from.cluster_id} className="border-t border-gms-line">
								<th scope="row" className="sticky left-0 z-[1] max-w-[11rem] bg-base-100 px-[0.75rem] py-[0.375rem] text-left font-normal">
									<span className="grid">
										<b className="text-[0.75rem] font-bold tracking-[0.04em] text-gms-navy">{from.code}</b>
										<span className="truncate text-[0.75rem] text-gms-ink-3">{from.name}</span>
									</span>
								</th>
								{items.map((to) => {
									const same = from.cluster_id === to.cluster_id;
									const k = cellKey(from.cluster_id, to.cluster_id);
									const value = grid.draft[k] ?? "";
									const invalid = grid.bad.has(k);
									return (
										<td key={to.cluster_id} className={`p-[0.25rem] text-center ${value && !invalid ? tint(Number(value)) : ""}`}>
											<input
												type="number"
												inputMode="numeric"
												min={DRIVE_MINUTES_MIN}
												max={DRIVE_MINUTES_MAX}
												step={1}
												value={value}
												placeholder={String(fallback)}
												aria-label={t("drive.cellLabel", { from: from.name, to: to.name })}
												aria-invalid={invalid || undefined}
												onChange={(e) => grid.type(from.cluster_id, to.cluster_id, e.target.value)}
												title={same ? t("drive.sameCluster") : undefined}
												className={`h-[2rem] w-[3.75rem] rounded-[0.375rem] border bg-base-100 text-center tabular-nums outline-none placeholder:text-gms-ink-3/60 focus:border-gms-navy focus:ring-2 focus:ring-gms-navy/20 ${
													invalid ? "border-gms-bad text-gms-bad" : same ? "border-dashed border-gms-ink-3/50" : "border-gms-line-2"
												}`}
											/>
										</td>
									);
								})}
							</tr>
						))}
					</tbody>
				</table>
			</div>
			<p className={`text-[0.75rem] ${grid.bad.size ? "font-medium text-gms-bad" : "text-gms-ink-3"}`}>
				{grid.bad.size ? t("drive.badCells", { min: DRIVE_MINUTES_MIN, max: DRIVE_MINUTES_MAX }) : t("drive.legend", { n: formatNumber(fallback) })}
			</p>
		</div>
	);
}
